import { Box, Flex } from '@chakra-ui/react';
import { CURVE_COLORS } from '@resistapp/client/components/plots/chip-view-constant';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { getKeyPressesFromMouseClick } from '@resistapp/client/utils/general';
import { scaleOrdinal } from '@visx/scale';
import { useEffect, useRef } from 'react';
import { BaseLegend } from './base-legend';
import type { KeysPressOptions } from './legend';

interface Props {
  genes: string[];
  selected?: string[] | null;
  onToggle?: (gene: string, keys: KeysPressOptions) => void;
  legendHeight?: string;
  id?: string;
  header?: string;
  description?: string;
  /** Optional custom colors per gene/assay (keyed by assay string) */
  colors?: Record<string, string>;
}

export function ChipGeneLegend({
  genes,
  selected,
  onToggle,
  legendHeight,
  id,
  header = 'Genes',
  description,
  colors,
}: Props) {
  const { getAssayInfo } = useAssayContext();

  const colorScale = scaleOrdinal<string, string>({
    domain: genes,
    range: genes.map((g, idx) => (colors?.[g] ? colors[g] : CURVE_COLORS[idx % CURVE_COLORS.length])),
  });

  const allSelected = !selected || selected.length === 0;
  const isSelected = (gene: string) => (allSelected ? true : selected.includes(gene));

  // Ref to scroll container to auto-scroll first selected gene into view
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Ensure we run the scroll logic only once - after the genes list becomes available.
  const hasScrolledRef = useRef(false);

  // Scroll first selected gene into view on initial render only.
  useEffect(() => {
    if (hasScrolledRef.current) return;
    if (!genes.length) return; // genes not yet populated

    const container = containerRef.current;
    if (!container) return;

    const firstSelected = allSelected ? genes[0] : selected[0];
    if (!firstSelected) return;

    const idx = genes.findIndex(g => g === firstSelected);
    if (idx < 0) return;

    const child = container.children[idx] as HTMLElement | undefined;
    if (!child) {
      hasScrolledRef.current = true;
      return;
    }

    if (container.scrollHeight > container.clientHeight) {
      container.scrollTo({ top: child.offsetTop, behavior: 'auto' });
    }

    hasScrolledRef.current = true;
    // eslint-disable-next-line exhaustive-deps-with-refs/exhaustive-deps
  }, [genes]);

  return (
    <BaseLegend id={id} header={header} description={description}>
      <Box
        maxHeight={legendHeight}
        overflowY="auto"
        overflowX="auto"
        className="download_legend-labels"
        ref={containerRef}
      >
        {genes.map(geneAssay => {
          const info = getAssayInfo(geneAssay);
          const label = info ? `${geneAssay} - ${info.gene}` : geneAssay;
          return (
            <Flex
              key={geneAssay}
              alignItems="center"
              mb="2px"
              pr={2}
              cursor={onToggle ? 'pointer' : undefined}
              opacity={isSelected(geneAssay) ? 1 : 0.4}
              style={{ userSelect: 'none' }}
              onClick={e => {
                if (!onToggle) return;
                const keys = getKeyPressesFromMouseClick(e);
                onToggle(geneAssay, keys);
              }}
            >
              <Box width="14px" height="14px" bg={colorScale(geneAssay)} borderRadius="2px" mr={2} />
              <Box flex="1" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis" userSelect="none">
                {label}
              </Box>
            </Flex>
          );
        })}
      </Box>
    </BaseLegend>
  );
}
